import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ContentUsersQueryKeys } from '@tabeeb/modules/contentUsers/requests'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { useDispatch } from '@tabeeb/store'
import { UpdateContentUserQualificationsRequest } from '@tabeeb/contracts/main-api/swagger-main-api'
import { useApi } from '@tabeeb/shared/utils/API'
import { InvitesQueryKeys } from '@tabeeb/modules/invites/requests'
import { updateUser } from '../../../users/actions'

export interface UseContentUserQualificationsProps {
  userId: string
  contentId: number
  qualificationIds: number[]
  enabled?: boolean
  onUpdateSuccess?: (newValue: UpdateContentUserQualificationsRequest) => void
}

const useContentUserQualifications = ({
  userId,
  contentId,
  qualificationIds,
  enabled = true,
  onUpdateSuccess,
}: UseContentUserQualificationsProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { certificateServiceApi, mainApi } = useApi()

  const query = useQuery({
    queryKey: [ContentUsersQueryKeys.ContentUserQualifications, userId, contentId, qualificationIds],
    queryFn: () =>
      certificateServiceApi.userQualifications.getByContent(
        { contentId },
        { UserId: userId, QualificationIds: qualificationIds }
      ),
    enabled: qualificationIds?.length > 0 && (enabled !== undefined ? enabled : true),
    staleTime: 30000,
  })

  const updateMutation = useMutation({
    mutationFn: (request: UpdateContentUserQualificationsRequest) => mainApi.contentUsers.updateQualifications(request),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update qualifications' }))
    },
    onSuccess: async (_, request) => {
      if (onUpdateSuccess) {
        onUpdateSuccess(request)
      }

      await queryClient.invalidateQueries({ queryKey: [InvitesQueryKeys.ContentInvites, contentId] })
      dispatch(updateUser({ id: request.UserId, requiredQualificationIds: request.QualificationIds }))

      dispatch(onAddSuccessNotification({ message: 'Qualifications updated' }))
    },
  })

  return {
    data: query.data,
    isLoading: query.isFetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    updateMutation,
  }
}

export default useContentUserQualifications
